/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */

import { Mesh, BoxBufferGeometry, FrontSide, SphereBufferGeometry, Object3D } from 'three';

import {
  Director,
  Materials,
  Stage,
  CapNames,
  AxisEditor,
  DefaultCaps,
  GizmoLock,
  OperationDOF,
  DefaultNavigators,
  BoundingBoxEditor,
  AbstractEditor,
  Events,
  FloorKind,
  SceneCompass,
  FloorDesc,
  CapDataDesc,
  ObjectClickedEvent,
  ObjectUnclickedEvent,
  ObjectReleasedEvent,
} from '@sick-davinci/glow';

import { debug } from './log';
import { CrownShapeCap } from '@sick-davinci/glow-crown';

/**
 * An active object to communicate to between edited object and UI
 */

export class ActiveObject {
  private _obj: Object3D = null;
  private _changeListener: Array<(obj: Object3D) => void> = [];

  set(obj: Object3D) {
    const { _obj: oldObj } = this;
    this._obj = obj;
    if (obj !== oldObj) {
      this._changeListener.forEach((cb) => cb(obj));
    }
  }

  exists() { return this._obj != null; }
  get() { return this._obj; }

  subscribe(cb: (obj: Object3D) => void) {
    this._changeListener.push(cb);
  }
}
/**
 * Return type of the default function
 */
export interface ShapeEditor {
  stage: Stage;
  director: Director;
  changeEditor: (type: 'Axis' | 'BoundingBox') => void;
  activeObject: ActiveObject;
  getEditor: () => AbstractEditor;
  onEditorChanged: (callback: () => void) => void;
  setHandleShapeByEngine: (func) => void;
}

/**
 * Demo: ShapeEditors
 * Description:
 *  This demo application shows how to use shape editors to change the geometry of 3D shapes.
 *  If connected to an AppEninge, the included app shows how to edit shapes using the ViewAPI.
 *  In order to confirm the successful edited shape it is send back to the client using color red.
 *
 * Features:
 *  - Orbit navigation with custom settings
 *  - SceneCompass
 *  - Capability handling
 *  - ViewAPI
 *  - Native threejs code
 *  - ShapeEditor
 *
 * Shape editing flow:
 * | Browser | Direction | Device |
 * | ------- | --------- | ------ |
 * | click on shape | -> | Event: View.OnPointer (CLICKED/PRIMARY) |
 * | set editor to shape | <- | View.installEditor |
 * | unclick shape | -> | Event: View.OnChange |
 */
export default ((viewParent: HTMLElement): ShapeEditor => {

  // * Setup the stage *
  const stage = new Stage(viewParent, {
    cameras: [{
      name: 'default',
      from: [280, 50, 200],
      to: [0, 0, 0],
      fov: 30,
      near: 1,
      far: 10000,
    }],
    scenes: [{ name: 'default' }],
    viewports: [{
      name: 'default',
      scene: 'default',
      camera: 'default',
      navigator: 'orbit',
      left: 0,
      top: 0,
      width: 1.0,
      height: 1.0,
      bgcolor: 0x292929,
    }],
  });

  // * Add an Orbit Navigator
  DefaultNavigators.initOrbit(stage, 'orbit', {
    pan: {
      speed: 8,
    },
    straightAhead: {
      speed: 10,
    },
  });

  // * Add the scene compass for better orientation
  SceneCompass.create(stage, 'default', 'default');

  // * Data handling using capabilities
  const director = new Director(stage);
  DefaultCaps.init(director);
  director.addCapability(new CrownShapeCap());

  director.handle(CapNames.LIGHT, { lightType: 'point', color: 0xff88aa, orientation: { position: [1500, 800, 1300] } });
  director.handle(CapNames.LIGHT, { lightType: 'ambient', color: 0xdddddd });
  // * Add a floor for better orientation
  director.handle(CapNames.FLOOR, {
    payload: {
      floor: FloorKind.WIREFRAME,
      size: 512,
      segments: 128,
      color: 0x555555,
    },
    orientation: {
      position: [0, -80, -0.1],
      rotation: [0, 0, 0],
    },
  } as CapDataDesc<FloorDesc>);

  const onEditorChangedListener = [];
  const notifyEditorChanged = () => {
    onEditorChangedListener.forEach((callback) => callback());
  };

  // * Create the AxisEditor
  let editor: AbstractEditor = new AxisEditor(stage);
  notifyEditorChanged();

  // * Track the active (selected) 3d object
  const activeObject = new ActiveObject();

  // * Add some boxes using native threejs code. Also add a parent/child relationship for the editor.
  stage.applyThreeDirect((scene) => {

    const box =
      new Mesh(
        new BoxBufferGeometry(25, 55, 85),
        Materials.create({ type: 'standard', params: { color: 0x66ddff, side: FrontSide, transparent: true, opacity: 0.3 } }),
      );
    box.name = 'box1';
    scene.add(box);

    const box2 =
      new Mesh(
        new BoxBufferGeometry(15, 15, 15),
        Materials.create({ type: 'standard', params: { color: 0xffdd66, side: FrontSide, transparent: true, opacity: 0.2 } }),
      );
    box2.position.set(-50, 70, -10);
    box2.rotation.set(0.1, 0.2, 0);
    box2.scale.set(2, 2, 2);
    box2.updateMatrix();
    box2.name = 'box2';
    (box2.userData.gizmoLock as GizmoLock) = {
      local: {
        translate: [OperationDOF.ALL],
        rotate: [OperationDOF.X],
        scale: [OperationDOF.Y],
      },
      global: {
        translate: [OperationDOF.ALL],
        rotate: [OperationDOF.ALL],
        scale: [OperationDOF.ALL],
      },
      view: {
        translate: [OperationDOF.ALL],
        rotate: [OperationDOF.ALL],
        scale: [OperationDOF.ALL],
      },
    };

    const boxDad =
      new Mesh(
        new BoxBufferGeometry(25, 25, 25),
        Materials.create({ type: 'standard', params: { color: 0x678678, side: FrontSide } }),
      );

    boxDad.position.set(50, -70, 10);
    boxDad.name = 'dad';

    const boxBaby =
      new Mesh(
        new BoxBufferGeometry(15, 15, 15),
        Materials.create({ type: 'standard', params: { color: 0x89a89a, side: FrontSide } }),
      );
    boxBaby.position.set(25, 0, 0);

    boxBaby.name = 'baby box';
    scene.add(boxDad);
    scene.addToParent(boxDad.name, boxBaby);

    scene.add(box2);

    const sphere =
      new Mesh(
        new SphereBufferGeometry(25, 32, 32),
        Materials.create({ type: 'standard', params: { color: 0xaaaaff, side: FrontSide, transparent: true, opacity: 0.2 } }),
      );
    sphere.position.set(80, 50, -10);
    sphere.name = 'sphere';
    scene.add(sphere);

    // * Start editing the box
    editor.setObject(box);
    activeObject.set(box);
  });

  let handleShapeByEngine = (object: Object3D) => false;

  // * User interaction: start editing the box when clicked on it
  stage.addEventListener(Events.OBJECTRELEASED, ({ detail: {object: {object}} }: CustomEvent<ObjectReleasedEvent>) => {
    if (object && object !== editor.getObject()) {

      if (!handleShapeByEngine(object)) {
        debug('you clicked on (locally created)', object);
        editor.setObject(object);
      }

      activeObject.set(object);
    }
  });

  // * Stop editing the shape when unclided
  stage.addEventListener(Events.OBJECTUNCLICKED, ({ detail }: CustomEvent<ObjectUnclickedEvent>) => {
    if (!detail.old || !handleShapeByEngine(detail.old.object)) {
      debug('unclicked!');
      editor.setObject(null);
      activeObject.set(null);
    }
  });
  const changeEditor = (type: 'Axis' | 'BoundingBox') => {
    const oldObject = editor.getObject();
    editor.setObject(null);

    editor = type === 'BoundingBox'
      ? new BoundingBoxEditor(stage)
      : new AxisEditor(stage);

    if (oldObject != null) {
      editor.setObject(oldObject);
    }

    notifyEditorChanged();
  };

  stage.start();  // start visualizing data

  return {
    stage,
    director,
    changeEditor,
    activeObject,
    getEditor: () => editor,
    onEditorChanged: (callback) => onEditorChangedListener.push(callback),
    setHandleShapeByEngine: (func: (obj: Object3D) => boolean) => { handleShapeByEngine = func; },
  };
});
